import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, RefreshCw, Loader2, FileText, Eye, EyeOff, Upload } from 'lucide-react';
import { API } from '../../App';
import { convertGoogleDriveLink } from '../../utils/imageUtils';

// Predefined Custom Field Templates
const CUSTOM_FIELD_TEMPLATES = {
  lehenga: {
    name: 'Lehenga Set',
    fields: [
      { field_name: 'Dupatta', field_value: 'Single' },
      { field_name: 'Blouse Type', field_value: 'Unstitched' },
      { field_name: 'Delivery Time', field_value: '10 Days' },
    ]
  },
  saree: {
    name: 'Saree',
    fields: [
      { field_name: 'Saree Length', field_value: '5.5 meters' },
      { field_name: 'Blouse Piece', field_value: '0.8 meters' },
      { field_name: 'Border Type', field_value: 'Zari' },
    ]
  },
  suit: {
    name: 'Salwar Suit',
    fields: [
      { field_name: 'Top Length', field_value: '2.5 meters' },
      { field_name: 'Bottom Length', field_value: '2.5 meters' },
      { field_name: 'Dupatta Length', field_value: '2.25 meters' },
      { field_name: 'Style', field_value: 'Unstitched' },
    ]
  },
  gown: {
    name: 'Gown/Anarkali',
    fields: [
      { field_name: 'Gown Length', field_value: '56 inches' },
      { field_name: 'Flair', field_value: '4 meters' },
      { field_name: 'Dupatta', field_value: 'Included' },
      { field_name: 'Sleeve Type', field_value: 'Full Sleeve' },
    ]
  }
};

// Direct image upload component — uploads to backend and returns the persistent URL.
const ImageUploader = ({ value, onChange, adminToken, label, testId }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputId = `${testId || 'image-uploader'}-input`;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, WEBP, GIF).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Max 10 MB.');
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="block text-sm font-medium">{label}</label>}
      {value ? (
        <div className="border border-border bg-white p-2" data-testid={`${testId}-current`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Current image:</span>
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs text-red-600 hover:underline"
              data-testid={`${testId}-remove`}
            >
              Remove
            </button>
          </div>
          <div className="max-h-56 overflow-hidden flex items-center justify-center bg-accent">
            <img src={convertGoogleDriveLink(value)} alt="Uploaded" className="max-h-56 object-contain" />
          </div>
          <label
            htmlFor={inputId}
            className={`mt-2 inline-flex items-center gap-2 px-3 py-2 border border-border hover:border-primary cursor-pointer text-sm ${uploading ? 'opacity-60' : ''}`}
          >
            <Upload size={14} />
            <span>Replace image</span>
          </label>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed cursor-pointer transition-colors ${uploading ? 'border-primary bg-primary/5' : 'border-border hover:border-primary hover:bg-accent/30'}`}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 size={28} className="text-primary animate-spin" />
              <p className="text-sm text-primary font-medium">Uploading… {progress}%</p>
            </>
          ) : (
            <>
              <Upload size={28} className="text-primary" />
              <p className="text-sm font-medium">Click to upload or drag & drop</p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WEBP, GIF · Max 10 MB</p>
            </>
          )}
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={uploading}
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </div>
  );
};

// Image Preview Component with loading state and retry
const ImagePreview = ({ url, alt }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    if (url) {
      setLoading(true);
      setError(false);
      // Add cache buster for retries
      const proxyUrl = convertGoogleDriveLink(url);
      const finalUrl = retryCount > 0 ? `${proxyUrl}${proxyUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : proxyUrl;
      setImageSrc(finalUrl);
    }
  }, [url, retryCount]);
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  return (
    <div className="mt-2 border border-border p-2" data-testid="image-preview-container">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-muted-foreground">Image Preview:</p>
        {(error || loading) && (
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
          >
            <RefreshCw size={12} />
            Retry
          </button>
        )}
      </div>
      
      <div className="relative min-h-[128px] bg-gray-50 flex items-center justify-center">
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <Loader2 size={24} className="animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading image...</span>
          </div>
        )}
        
        {!error && (
          <img
            src={imageSrc}
            alt={alt || "Preview"}
            className={`h-32 w-auto object-cover transition-opacity ${loading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            data-testid="product-image-preview"
          />
        )}
        
        {error && (
          <div className="text-center p-4">
            <p className="text-xs text-destructive mb-2">Image failed to load.</p>
            <p className="text-xs text-muted-foreground mb-2">Check if the Google Drive link has "Anyone with the link" permission.</p>
            <button
              type="button"
              onClick={handleRetry}
              className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminProductsPage = ({ adminToken }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    base_price: 0,
    fabric: '',
    work_type: '',
    main_image: '',
    images: [],
    featured: false,
    variants: [],
    image_fit: 'cover',
    image_zoom: 1.0,
    image_position: 'center',
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchProducts = async () => {
    try {
      // Use admin endpoint to get all products including hidden ones
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (productId, currentVisibility) => {
    try {
      const response = await axios.patch(
        `${API}/admin/products/${productId}/visibility`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      fetchProducts();
    } catch (error) {
      console.error('Failed to toggle visibility', error);
      toast.error('Failed to update product visibility');
    }
  };

  const handleAddVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { color: '', size: '', sku: '', price: 0, stock: 0, images: [], custom_fields: [] }
      ]
    });
  };

  const handleRemoveVariant = (index) => {
    const newVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: newVariants });
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index][field] = value;
    setFormData({ ...formData, variants: newVariants });
  };

  const handleAddCustomField = (variantIndex) => {
    const newVariants = [...formData.variants];
    if (!newVariants[variantIndex].custom_fields) {
      newVariants[variantIndex].custom_fields = [];
    }
    newVariants[variantIndex].custom_fields.push({ field_name: '', field_value: '' });
    setFormData({ ...formData, variants: newVariants });
  };

  const handleRemoveCustomField = (variantIndex, fieldIndex) => {
    const newVariants = [...formData.variants];
    newVariants[variantIndex].custom_fields = newVariants[variantIndex].custom_fields.filter((_, i) => i !== fieldIndex);
    setFormData({ ...formData, variants: newVariants });
  };

  const handleCustomFieldChange = (variantIndex, fieldIndex, key, value) => {
    const newVariants = [...formData.variants];
    newVariants[variantIndex].custom_fields[fieldIndex][key] = value;
    setFormData({ ...formData, variants: newVariants });
  };

  const handleApplyTemplate = (variantIndex, templateKey) => {
    const template = CUSTOM_FIELD_TEMPLATES[templateKey];
    if (!template) return;
    
    const newVariants = [...formData.variants];
    // Deep copy the template fields to avoid reference issues
    newVariants[variantIndex].custom_fields = template.fields.map(f => ({
      field_name: f.field_name,
      field_value: f.field_value
    }));
    setFormData({ ...formData, variants: newVariants });
    toast.success(`Applied "${template.name}" template`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Convert Google Drive links for main image
      const updatedFormData = {
        ...formData,
        main_image: convertGoogleDriveLink(formData.main_image),
        images: formData.images.map(img => convertGoogleDriveLink(img))
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, updatedFormData, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/admin/products`, updatedFormData, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Product created successfully!');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    // Ensure each variant has custom_fields array initialized
    const variantsWithCustomFields = (product.variants || []).map(v => ({
      ...v,
      custom_fields: v.custom_fields || []
    }));
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      base_price: product.base_price,
      fabric: product.fabric,
      work_type: product.work_type,
      main_image: product.main_image,
      images: product.images || [],
      featured: product.featured,
      variants: variantsWithCustomFields,
      image_fit: product.image_fit || 'cover',
      image_zoom: typeof product.image_zoom === 'number' ? product.image_zoom : 1.0,
      image_position: product.image_position || 'center',
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Bridal',
      base_price: 0,
      fabric: '',
      work_type: '',
      main_image: '',
      images: [],
      featured: false,
      variants: [],
      image_fit: 'cover',
      image_zoom: 1.0,
      image_position: 'center',
    });
    setEditingProduct(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="py-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold mb-2">Products Management</h1>
          <p className="text-muted-foreground">Manage your product catalog</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90 transition-colors flex items-center space-x-2"
          data-testid="add-product-button"
        >
          <Plus size={20} />
          <span>Add Product</span>
        </button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-border overflow-hidden"
            data-testid={`product-item-${product.id}`}
          >
            <div className="aspect-[4/3] bg-accent overflow-hidden relative">
              <img src={convertGoogleDriveLink(product.main_image)} alt={product.name} className="w-full h-full object-cover" />
              {/* Hidden Badge */}
              {product.is_visible === false && (
                <div className="absolute top-2 left-2 bg-gray-800 text-white px-2 py-1 text-xs font-semibold flex items-center gap-1">
                  <EyeOff size={12} />
                  Hidden
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-heading font-semibold text-lg">{product.name}</h3>
                {/* Visibility Toggle */}
                <button
                  onClick={() => handleToggleVisibility(product.id, product.is_visible)}
                  className={`p-1.5 rounded transition-colors ${
                    product.is_visible === false 
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title={product.is_visible === false ? 'Click to show on website' : 'Click to hide from website'}
                  data-testid={`toggle-visibility-${product.id}`}
                >
                  {product.is_visible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{product.category}</p>
              <p className="text-primary font-semibold mb-3">&#8377;{product.base_price.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground mb-3">{product.variants.length} variants</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="flex-1 border-2 border-primary text-primary hover:bg-primary hover:text-white py-2 transition-colors flex items-center justify-center space-x-1"
                  data-testid={`edit-product-${product.id}`}
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="flex-1 border-2 border-destructive text-destructive hover:bg-destructive hover:text-white py-2 transition-colors flex items-center justify-center space-x-1"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" data-testid="product-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-2xl font-heading font-bold">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                    data-testid="product-category-input"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Base Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                    data-testid="product-price-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Fabric *</label>
                  <input
                    type="text"
                    required
                    value={formData.fabric}
                    onChange={(e) => setFormData({ ...formData, fabric: e.target.value })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                    data-testid="product-fabric-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Work Type *</label>
                  <input
                    type="text"
                    required
                    value={formData.work_type}
                    onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary px-4 py-3 outline-none"
                    data-testid="product-work-type-input"
                  />
                </div>
              </div>

              <div>
                <ImageUploader
                  label="Main Image *"
                  value={formData.main_image}
                  onChange={(url) => setFormData({ ...formData, main_image: url })}
                  adminToken={adminToken}
                  testId="product-main-image-uploader"
                />
              </div>

              {/* Additional Images (Slider) */}
              <div data-testid="product-additional-images-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Additional Images (Slider)</label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, images: [...(formData.images || []), ''] })}
                    className="text-sm text-primary hover:underline inline-flex items-center"
                    data-testid="add-extra-image-btn"
                  >
                    + Add Image
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Add 1 or more additional images — customers will be able to slide through them on the product page.
                </p>
                {(formData.images || []).length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No additional images added yet.</p>
                )}
                <div className="space-y-3">
                  {(formData.images || []).map((img, idx) => (
                    <div key={idx} className="border border-border p-3" data-testid={`extra-image-row-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <ImageUploader
                            value={img}
                            onChange={(url) => {
                              const next = [...(formData.images || [])];
                              next[idx] = url;
                              setFormData({ ...formData, images: next });
                            }}
                            adminToken={adminToken}
                            testId={`extra-image-uploader-${idx}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const next = (formData.images || []).filter((_, i) => i !== idx);
                            setFormData({ ...formData, images: next });
                          }}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 h-fit"
                          title="Remove this image"
                          data-testid={`remove-extra-image-${idx}`}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Display Settings */}
              <div className="border border-border p-4 bg-accent/30" data-testid="image-display-settings">
                <h3 className="text-lg font-heading font-semibold mb-1">Image Display Settings</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Control how product images appear on the customer product page. Applies to the main image and all additional slider images.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Fit Mode</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_fit: 'cover' })}
                        className={`px-3 py-2 border text-sm transition-colors ${formData.image_fit === 'cover' ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary'}`}
                        data-testid="image-fit-cover"
                      >
                        Fill Frame (Cover)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_fit: 'contain' })}
                        className={`px-3 py-2 border text-sm transition-colors ${formData.image_fit === 'contain' ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary'}`}
                        data-testid="image-fit-contain"
                      >
                        Show Full (Contain)
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.image_fit === 'cover'
                        ? 'Fills the frame — may crop edges. Best for landscape/wide images.'
                        : 'Shows full image — may have whitespace. Best for vertical/portrait images.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Vertical Focus (for Cover mode)</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['top', 'center', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          type="button"
                          onClick={() => setFormData({ ...formData, image_position: pos })}
                          className={`px-2 py-2 border text-sm capitalize transition-colors ${formData.image_position === pos ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary'}`}
                          data-testid={`image-position-${pos}`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Which part of the image stays visible when cropped.</p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Zoom</label>
                    <span className="text-sm font-mono text-muted-foreground" data-testid="image-zoom-value">
                      {(formData.image_zoom || 1.0).toFixed(2)}×
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_zoom: Math.max(0.5, Math.round(((formData.image_zoom || 1.0) - 0.05) * 100) / 100) })}
                      className="w-9 h-9 border border-border hover:border-primary flex items-center justify-center text-lg font-semibold"
                      data-testid="image-zoom-out"
                      aria-label="Zoom out"
                    >
                      −
                    </button>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.05"
                      value={formData.image_zoom || 1.0}
                      onChange={(e) => setFormData({ ...formData, image_zoom: parseFloat(e.target.value) })}
                      className="flex-1 accent-primary"
                      data-testid="image-zoom-slider"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_zoom: Math.min(2, Math.round(((formData.image_zoom || 1.0) + 0.05) * 100) / 100) })}
                      className="w-9 h-9 border border-border hover:border-primary flex items-center justify-center text-lg font-semibold"
                      data-testid="image-zoom-in"
                      aria-label="Zoom in"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_zoom: 1.0 })}
                      className="px-3 h-9 border border-border hover:border-primary text-sm"
                      data-testid="image-zoom-reset"
                      title="Reset zoom to 1.00×"
                    >
                      Reset
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Lower value shows more of the surroundings; higher value zooms in.</p>
                </div>

                {/* Live preview */}
                {formData.main_image && (
                  <div data-testid="image-display-preview">
                    <p className="text-sm font-medium mb-2">Live Preview</p>
                    <div className="relative aspect-[3/4] max-w-xs overflow-hidden bg-accent border border-border">
                      <img
                        src={convertGoogleDriveLink(formData.main_image)}
                        alt="Preview"
                        className="w-full h-full transition-transform duration-200"
                        style={{
                          objectFit: formData.image_fit || 'cover',
                          objectPosition: `center ${formData.image_position || 'center'}`,
                          transform: `scale(${Math.min(2, Math.max(0.5, formData.image_zoom || 1))})`,
                        }}
                        draggable={false}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="mr-2"
                    data-testid="product-featured-input"
                  />
                  Featured Product
                </label>
              </div>

              {/* Variants Section */}
              <div className="border-t border-border pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-heading font-semibold">Product Variants</h3>
                  <button
                    type="button"
                    onClick={handleAddVariant}
                    className="bg-secondary text-secondary-foreground px-4 py-2 hover:bg-secondary/90 transition-colors"
                    data-testid="add-variant-button"
                  >
                    Add Variant
                  </button>
                </div>

                {formData.variants.map((variant, index) => (
                  <div key={index} className="bg-accent p-4 mb-4 relative">
                    <button
                      type="button"
                      onClick={() => handleRemoveVariant(index)}
                      className="absolute top-2 right-2 text-destructive hover:text-destructive/80"
                    >
                      <Trash2 size={18} />
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Color</label>
                        <input
                          type="text"
                          value={variant.color}
                          onChange={(e) => handleVariantChange(index, 'color', e.target.value)}
                          className="w-full border border-border px-3 py-2 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Size</label>
                        <input
                          type="text"
                          value={variant.size}
                          onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                          className="w-full border border-border px-3 py-2 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Design No</label>
                        <input
                          type="text"
                          value={variant.sku}
                          onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                          className="w-full border border-border px-3 py-2 outline-none"
                          placeholder="e.g. AF-2026-001"
                          data-testid={`variant-design-no-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.price}
                          onChange={(e) => handleVariantChange(index, 'price', parseFloat(e.target.value))}
                          className="w-full border border-border px-3 py-2 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Stock</label>
                        <input
                          type="number"
                          value={variant.stock}
                          onChange={(e) => handleVariantChange(index, 'stock', parseInt(e.target.value))}
                          className="w-full border border-border px-3 py-2 outline-none"
                        />
                      </div>
                    </div>

                    {/* Custom Fields Section */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h4 className="text-sm font-semibold text-muted-foreground">Custom Fields</h4>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Template Dropdown */}
                          <div className="relative">
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleApplyTemplate(index, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              className="text-xs border border-primary/30 bg-primary/5 text-primary px-3 py-1.5 pr-8 appearance-none cursor-pointer hover:bg-primary/10 transition-colors"
                              data-testid={`template-select-${index}`}
                            >
                              <option value="">Apply Template</option>
                              {Object.entries(CUSTOM_FIELD_TEMPLATES).map(([key, template]) => (
                                <option key={key} value={key}>{template.name}</option>
                              ))}
                            </select>
                            <FileText size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-primary pointer-events-none" />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddCustomField(index)}
                            className="text-xs bg-primary/10 text-primary px-3 py-1.5 hover:bg-primary/20 transition-colors flex items-center gap-1"
                            data-testid={`add-custom-field-${index}`}
                          >
                            <Plus size={14} />
                            Add Field
                          </button>
                        </div>
                      </div>
                      
                      {(variant.custom_fields || []).map((field, fieldIndex) => (
                        <div key={fieldIndex} className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Field Name (e.g., Dupatta Length)"
                            value={field.field_name}
                            onChange={(e) => handleCustomFieldChange(index, fieldIndex, 'field_name', e.target.value)}
                            className="flex-1 border border-border px-3 py-2 text-sm outline-none focus:border-primary"
                            data-testid={`custom-field-name-${index}-${fieldIndex}`}
                          />
                          <input
                            type="text"
                            placeholder="Value (e.g., 2.5 meters)"
                            value={field.field_value}
                            onChange={(e) => handleCustomFieldChange(index, fieldIndex, 'field_value', e.target.value)}
                            className="flex-1 border border-border px-3 py-2 text-sm outline-none focus:border-primary"
                            data-testid={`custom-field-value-${index}-${fieldIndex}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomField(index, fieldIndex)}
                            className="text-destructive hover:text-destructive/80 p-2"
                            data-testid={`remove-custom-field-${index}-${fieldIndex}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      
                      {(!variant.custom_fields || variant.custom_fields.length === 0) && (
                        <p className="text-xs text-muted-foreground italic">No custom fields. Click "Add Field" to add custom attributes like Dupatta Length, Blouse Type, etc.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex space-x-4 pt-6">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground py-3 hover:bg-primary/90 transition-colors font-medium"
                  data-testid="submit-product-button"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border-2 border-border hover:border-primary py-3 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
